import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fmtDuration } from "@/lib/api";
import { PortalShell } from "@/pages/ClientPortalShell";
import { ArrowLeft, Scissors, Play, Pause, Loader2, Trash2, Sparkles, X, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function MyStudioEditor() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [cuts, setCuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inTime, setInTime] = useState(null);
  const [outTime, setOutTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [genJob, setGenJob] = useState(null);
  const videoRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([
        api.get(`/box/videos/${videoId}`),
        api.get(`/box/videos/${videoId}/cuts`),
      ]);
      setVideo(v.data);
      setCuts(c.data.cuts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load video");
      navigate("/portal/studio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [videoId]);

  // Keyboard shortcuts: I = Mark IN, O = Mark OUT, Space = Play/Pause
  // Ignored while typing in inputs/textareas or holding modifier keys.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "i") { e.preventDefault(); markIn(); }
      else if (k === "o") { e.preventDefault(); markOut(); }
      else if (k === " ") { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // markIn / markOut / togglePlay all read from refs + latest state via closures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inTime, outTime]);

  // Poll clip-job while running
  useEffect(() => {
    if (!genJob) return;
    if (["done", "failed"].includes(genJob.status)) return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/box/clip-jobs/${genJob.id}`);
        setGenJob(r.data);
        if (["done", "failed"].includes(r.data.status)) {
          clearInterval(id);
          if (r.data.status === "done") {
            toast.success(`Generated ${r.data.generated} clip${r.data.generated === 1 ? "" : "s"}`);
            // Reload cuts (they're preserved) and navigate to Box
            setTimeout(() => navigate("/portal/studio"), 1200);
          } else {
            toast.error("Clip generation failed: " + (r.data.error || "unknown"));
          }
        }
      } catch (_e) { /* transient poll error; retry next tick */ }
    }, 2000);
    return () => clearInterval(id);
  }, [genJob, navigate]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (v) setCurrentTime(v.currentTime);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const markIn = () => {
    const t = videoRef.current?.currentTime ?? 0;
    setInTime(t);
    if (outTime !== null && t >= outTime) setOutTime(null);
  };
  const markOut = () => {
    const t = videoRef.current?.currentTime ?? 0;
    if (inTime === null) return toast.error("Mark IN first");
    if (t <= inTime) return toast.error("OUT must be after IN");
    setOutTime(t);
  };

  const saveCut = async (label) => {
    if (inTime === null || outTime === null) {
      toast.error("Set IN and OUT first");
      return;
    }
    try {
      await api.post(`/box/videos/${videoId}/cuts`, {
        start_time: inTime,
        end_time: outTime,
        category: "clip",
        sub_label: label || "",
      });
      toast.success("Cut saved");
      setInTime(null);
      setOutTime(null);
      const r = await api.get(`/box/videos/${videoId}/cuts`);
      setCuts(r.data.cuts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save cut");
    }
  };

  const delCut = async (cutId) => {
    try {
      await api.delete(`/box/cuts/${cutId}`);
      setCuts((p) => p.filter((c) => c.id !== cutId));
    } catch {
      toast.error("Failed to delete cut");
    }
  };

  const renameVideo = async () => {
    const cur = video?.original_name || "";
    const next = window.prompt("Rename video", cur);
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === cur.trim()) return;
    try {
      await api.patch(`/box/videos/${videoId}`, { original_name: trimmed });
      setVideo((v) => (v ? { ...v, original_name: trimmed } : v));
      toast.success("Renamed");
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const deleteVideo = async () => {
    if (!window.confirm(`Delete "${video?.original_name || "this video"}" and all its clips? This can't be undone.`)) return;
    try {
      await api.delete(`/box/videos/${videoId}`);
      toast.success("Deleted");
      navigate("/portal/studio");
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const generate = async () => {
    if (cuts.length === 0) {
      toast.error("Save at least one cut first");
      return;
    }
    try {
      const r = await api.post(`/box/videos/${videoId}/generate-clips`, {
        cut_ids: cuts.map((c) => c.id),
      });
      const j = await api.get(`/box/clip-jobs/${r.data.job_id}`);
      setGenJob(j.data);
      toast.success(`Rendering ${r.data.total} clip${r.data.total === 1 ? "" : "s"}…`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start");
    }
  };

  const duration = video?.duration || 0;

  if (loading) {
    return <PortalShell><div className="text-stone-500 py-12 text-center">Loading…</div></PortalShell>;
  }

  return (
    <PortalShell>
      <div className="mb-6">
        <Link to="/portal/studio" className="text-stone-400 hover:text-stone-100 text-xs uppercase tracking-widest inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Rodeo Box
        </Link>
      </div>

      <div className="border-l-4 border-orange-600 pl-5 mb-6" data-testid="editor-header">
        <div className="label-tiny mb-2">Trim editor</div>
        <div className="flex items-start gap-3 flex-wrap">
          <h1 className="font-display uppercase text-4xl font-bold leading-[0.95] break-words">
            {video?.original_name}
          </h1>
          <div className="flex items-center gap-1 pt-2">
            <button
              onClick={renameVideo}
              className="p-1.5 border border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400 transition-colors"
              title="Rename video"
              data-testid="editor-video-rename"
            >
              <Pencil size={12} />
            </button>
            <button
              onClick={deleteVideo}
              className="p-1.5 border border-stone-700 hover:border-rose-600 text-stone-400 hover:text-rose-400 transition-colors"
              title="Delete video (removes all cuts + clips)"
              data-testid="editor-video-delete"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <div className="border border-stone-800 bg-black">
            <video
              ref={videoRef}
              src={video?.stream_url}
              controls
              onTimeUpdate={onTimeUpdate}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              className="w-full aspect-video"
              data-testid="editor-video"
            />
          </div>

          {/* Trim rail */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-widest text-stone-500">
              <span>0:00</span>
              <span className="font-mono-tc text-orange-500">{fmtDuration(currentTime)}</span>
              <span>{fmtDuration(duration)}</span>
            </div>
            <TrimRail
              duration={duration}
              inTime={inTime}
              outTime={outTime}
              currentTime={currentTime}
              cuts={cuts}
              onScrub={(t) => { if (videoRef.current) videoRef.current.currentTime = t; }}
            />
          </div>

          {/* In / Out controls */}
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <button onClick={togglePlay} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="editor-play" title="Play / Pause (Space)">
              {playing ? <Pause size={13} /> : <Play size={13} />}
              {playing ? "Pause" : "Play"}
              <kbd className="hidden sm:inline ml-1 px-1 py-0.5 border border-stone-700 text-[9px] font-mono-tc text-stone-400 leading-none">Space</kbd>
            </button>
            <button onClick={markIn} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="editor-mark-in" title="Mark IN at current time (I)">
              Mark IN <span className="font-mono-tc text-orange-400">{inTime === null ? "—" : fmtDuration(inTime)}</span>
              <kbd className="hidden sm:inline ml-1 px-1 py-0.5 border border-stone-700 text-[9px] font-mono-tc text-stone-400 leading-none">I</kbd>
            </button>
            <button onClick={markOut} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="editor-mark-out" title="Mark OUT at current time (O)">
              Mark OUT <span className="font-mono-tc text-orange-400">{outTime === null ? "—" : fmtDuration(outTime)}</span>
              <kbd className="hidden sm:inline ml-1 px-1 py-0.5 border border-stone-700 text-[9px] font-mono-tc text-stone-400 leading-none">O</kbd>
            </button>
            <CutSaveInline canSave={inTime !== null && outTime !== null && outTime > inTime} onSave={saveCut} />
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-stone-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="editor-shortcuts-hint">
            <span><kbd className="px-1 py-0.5 border border-stone-700 text-stone-300 font-mono-tc">I</kbd> = Mark In</span>
            <span><kbd className="px-1 py-0.5 border border-stone-700 text-stone-300 font-mono-tc">O</kbd> = Mark Out</span>
            <span><kbd className="px-1 py-0.5 border border-stone-700 text-stone-300 font-mono-tc">Space</kbd> = Play / Pause</span>
          </div>
        </div>

        {/* Right rail: cuts list + generate */}
        <div className="space-y-4">
          <div className="border border-stone-800 p-4" data-testid="cuts-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display uppercase font-bold">Saved cuts</div>
              <span className="font-mono-tc text-orange-500 text-lg">{cuts.length}</span>
            </div>
            {cuts.length === 0 ? (
              <div className="text-stone-500 text-xs py-4 text-center">
                Mark IN + OUT + save to add a cut.
              </div>
            ) : (
              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {cuts.map((c) => (
                  <CutRow key={c.id} cut={c} onDelete={() => delCut(c.id)} onSeek={(t) => { if (videoRef.current) videoRef.current.currentTime = t; }} />
                ))}
              </div>
            )}
          </div>

          {genJob && !["done", "failed"].includes(genJob.status) ? (
            <div className="border border-orange-700 bg-orange-600/10 p-4" data-testid="gen-progress">
              <div className="inline-flex items-center gap-2 mb-2">
                <Loader2 size={14} className="animate-spin text-orange-500" />
                <span className="font-display uppercase font-bold">Rendering…</span>
              </div>
              <div className="text-xs text-stone-400">
                {genJob.generated}/{genJob.total} clips done
              </div>
            </div>
          ) : (
            <button
              onClick={generate}
              disabled={cuts.length === 0 || !!genJob}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm py-3"
              data-testid="editor-generate"
            >
              <Sparkles size={14} /> Generate {cuts.length} clip{cuts.length === 1 ? "" : "s"}
            </button>
          )}

          <div className="text-[11px] text-stone-500 leading-relaxed">
            Every generated clip gets a subtle &ldquo;Made with Rodeo Cut Studio&rdquo; badge in the bottom-right corner.
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

function CutSaveInline({ canSave, onSave }) {
  const [label, setLabel] = useState("");
  return (
    <div className="flex items-center gap-2 ml-auto">
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (optional)"
        className="field-input !py-1 text-xs w-40"
        data-testid="editor-cut-label"
      />
      <button
        disabled={!canSave}
        onClick={() => { onSave(label); setLabel(""); }}
        className="btn-primary inline-flex items-center gap-1 text-xs !py-2"
        data-testid="editor-save-cut"
      >
        <Scissors size={11} /> Save cut
      </button>
    </div>
  );
}

function CutRow({ cut, onDelete, onSeek }) {
  return (
    <div className="border border-stone-800 p-2.5 flex items-center gap-2 group" data-testid={`cut-row-${cut.id}`}>
      <button
        type="button"
        onClick={() => onSeek(cut.start_time)}
        className="flex-1 min-w-0 text-left"
      >
        <div className="text-sm font-display uppercase font-bold truncate">
          {cut.sub_label || "Untitled"}
        </div>
        <div className="text-[10px] text-stone-500 font-mono-tc">
          {fmtDuration(cut.start_time)} → {fmtDuration(cut.end_time)}
          <span className="text-stone-600"> · {fmtDuration(cut.end_time - cut.start_time)}</span>
        </div>
      </button>
      <button
        onClick={onDelete}
        className="text-stone-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`cut-del-${cut.id}`}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function TrimRail({ duration, inTime, outTime, currentTime, cuts, onScrub }) {
  const railRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pct = (t) => (duration > 0 ? (t / duration) * 100 : 0);

  const handleClick = (e) => {
    if (!duration || !railRef.current) return;
    const rect = railRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const t = Math.max(0, Math.min(duration, (x / rect.width) * duration));
    onScrub(t);
  };

  const onDown = (e) => {
    setDragging(true);
    handleClick(e);
  };
  const onMove = (e) => {
    if (dragging) handleClick(e);
  };
  const onUp = () => setDragging(false);

  return (
    <div
      ref={railRef}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      className="relative h-10 bg-stone-900 border border-stone-800 cursor-pointer select-none"
      data-testid="trim-rail"
    >
      {/* Cuts as green segments */}
      {cuts.map((c) => (
        <div
          key={c.id}
          className="absolute top-0 bottom-0 bg-emerald-600/25 border-x border-emerald-500/60"
          style={{ left: `${pct(c.start_time)}%`, width: `${pct(c.end_time - c.start_time)}%` }}
          title={c.sub_label || "cut"}
        />
      ))}
      {/* In/Out selection */}
      {inTime !== null && outTime !== null && (
        <div
          className="absolute top-0 bottom-0 bg-orange-600/40 border-x-2 border-orange-500"
          style={{ left: `${pct(inTime)}%`, width: `${pct(outTime - inTime)}%` }}
        />
      )}
      {inTime !== null && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-orange-500"
          style={{ left: `${pct(inTime)}%` }}
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest text-orange-400 font-mono-tc">IN</div>
        </div>
      )}
      {outTime !== null && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-orange-500"
          style={{ left: `${pct(outTime)}%` }}
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest text-orange-400 font-mono-tc">OUT</div>
        </div>
      )}
      {/* Playhead */}
      <div
        className="absolute top-0 bottom-0 w-px bg-stone-100 pointer-events-none"
        style={{ left: `${pct(currentTime)}%` }}
      />
    </div>
  );
}
